import * as React from "react";

import { useAnalytics, useAuth, useNavigation } from "@/providers";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { useCollaborationBar } from "@/shared/state/collaborationBar";

/** The affordance stays visible and inert rather than disappearing, because
 *  "fork first" is the thing worth learning. */
export const NOT_YOURS_HINT =
  "Only your own threads open in the sidebar. Fork the thread to continue.";

interface OpenThreadInSidebar {
  isOwnThread: boolean;

  /** Safe to call when ``isOwnThread`` is false, but callers should not offer
   *  it: the sidebar has no read-only mode, so it would open a composer the
   *  API refuses. */
  openHere: () => void;
}

/**
 * The "move this thread into the chat sidebar" action, shared by the thread
 * header's button and the sidebar's open-on-the-page notice.
 *
 * Navigating the page away is part of the action, not a caller's follow-up: a
 * thread lives in exactly one pane, so adopting it here means sending the page
 * off it.
 */
export function useOpenThreadInSidebar(
  thread: AgentThreadRecord,
): OpenThreadInSidebar {
  const { currentUser } = useAuth();
  const { open: openCollaborationBar } = useCollaborationBar();
  const { trackEvent } = useAnalytics();
  const { goto } = useNavigation();

  const isOwnThread =
    currentUser !== null && thread.created_by === currentUser.user_id;

  const openHere = React.useCallback(() => {
    trackEvent("AgentThreadOpenedInSidebar");
    openCollaborationBar({
      mode: "ai-agent-chat",
      threadId: thread.thread_id,
      view: "thread",
    });
    goto.agentThreads();
  }, [openCollaborationBar, goto, trackEvent, thread.thread_id]);

  return { isOwnThread, openHere };
}
